<template>
    <div>
        <section class="section-main">
            <AdminFiltersBar v-model="filtersState" :filters="filtersConfig" />

            <AdminPaginationControls
                :items="filteredResources"
                :initial-page="initialPage"
                :initial-page-size="initialPageSize"
                :show-page-size="false"
            >
                <template #default="{ pagedItems, total }">
                    <div
                        class="overflow-auto rounded-md border border-gray-200 bg-white"
                    >
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-200">
                                <tr>
                                    <th class="w-14 p-4" scope="col">
                                        <span class="sr-only">Icon</span>
                                    </th>
                                    <th
                                        scope="col"
                                        class="p-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
                                    >
                                        Title
                                    </th>
                                    <th
                                        scope="col"
                                        class="p-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
                                    >
                                        Slug
                                    </th>
                                    <th
                                        scope="col"
                                        class="p-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
                                    >
                                        <span
                                            class="inline-flex items-center gap-1"
                                        >
                                            <span># of Lessons</span>
                                            <span
                                                v-tippy="{
                                                    placement: 'top',
                                                    theme: 'resource-lessons-tooltip',
                                                }"
                                                content="Number of lessons using this resource"
                                            >
                                                <InformationCircleIcon
                                                    class="h-4 w-4"
                                                    aria-hidden="true"
                                                />
                                                <span class="sr-only"
                                                    >Number of lessons using
                                                    this resource</span
                                                >
                                            </span>
                                        </span>
                                    </th>
                                    <th
                                        scope="col"
                                        class="p-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
                                    >
                                        Status
                                    </th>
                                    <th class="w-12 p-4" scope="col">
                                        <span class="sr-only">Actions</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-100">
                                <tr
                                    v-for="resource in pagedItems"
                                    :key="resource.id"
                                    class="hover:bg-gray-50"
                                >
                                    <td>
                                        <svg-icon
                                            class="mx-3 px-1 rounded-md"
                                            :class="
                                                resourceIcon(resource.type)
                                                    .bgColor
                                            "
                                            :icon="
                                                resourceIcon(resource.type)
                                                    .icon
                                            "
                                            size="lg"
                                            aria-hidden="true"
                                        />
                                    </td>
                                    <td
                                        class="p-4 text-sm font-medium text-gray-900"
                                    >
                                        {{ resource.title || "-" }}
                                    </td>
                                    <td class="p-4 text-sm text-gray-700">
                                        {{ resource.slug || "-" }}
                                    </td>
                                    <td class="p-4 text-sm">
                                        <a
                                            v-if="resource.lesson_count > 0"
                                            href="#"
                                            class="font-medium text-blue-500 underline"
                                            :aria-label="`View ${resource.lesson_count} attached lessons for ${resource.title}`"
                                            @click.prevent="
                                                openLessonsModal(resource)
                                            "
                                            >{{ resource.lesson_count }}</a
                                        >
                                        <span v-else class="text-gray-500"
                                            >0</span
                                        >
                                    </td>
                                    <td class="p-4 text-sm">
                                        <span
                                            class="inline-block rounded-full px-3 py-1 text-xs font-medium"
                                            :class="
                                                statusBadge(resource.status)
                                            "
                                        >
                                            {{
                                                (
                                                    resource.status || "ready"
                                                ).toUpperCase()
                                            }}
                                        </span>
                                    </td>
                                    <td class="p-4 text-right">
                                        <button
                                            type="button"
                                            :class="[
                                                'p-1 transition-colors',
                                                attachmentCount(resource) === 0
                                                    ? 'text-red-700 hover:text-red-800'
                                                    : 'pointer-events-none text-gray-300',
                                            ]"
                                            :disabled="
                                                attachmentCount(resource) > 0
                                            "
                                            :aria-label="`Delete resource: ${resource.title}`"
                                            @click="openDeleteModal(resource)"
                                            v-tippy
                                            :content="deleteTooltip(resource)"
                                        >
                                            <SvgIcon
                                                icon="trash"
                                                size="md"
                                                weight="normal"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    </td>
                                </tr>

                                <tr v-if="total === 0">
                                    <td
                                        colspan="6"
                                        class="px-4 py-8 text-center text-sm text-gray-500"
                                    >
                                        No resources found.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </template>
            </AdminPaginationControls>

            <!-- Lesson Modal Popup -->
            <div
                v-if="isLessonsModalOpen"
                class="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50 p-4"
                role="dialog"
                aria-modal="true"
                aria-labelledby="lessons-modal-title"
                @click.self="closeLessonsModal"
            >
                <div
                    class="relative z-50 w-full max-w-2xl rounded-lg bg-white p-12 shadow-2xl"
                >
                    <button
                        type="button"
                        class="absolute right-3 top-3 focus:top-3 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                        aria-label="Close attached lessons modal"
                        @click.prevent.stop="closeLessonsModal"
                    >
                        <span
                            aria-hidden="true"
                            class="block text-2xl leading-none"
                            >&times;</span
                        >
                    </button>

                    <h3
                        id="lessons-modal-title"
                        class="text-xl font-bold text-gray-700"
                    >
                        {{ selectedResource?.title || "Resource" }} — Attached
                        Lessons
                    </h3>
                    <p class="mt-2 text-sm text-gray-500">
                        These are the lessons that this Resource is attached to.
                        If you need to delete the file you will manually need to
                        remove the Resource from each lesson within the admin.
                    </p>

                    <div
                        class="mt-6 overflow-hidden rounded-md border border-gray-200"
                    >
                        <div
                            v-for="lesson in selectedResourceLessons"
                            :key="lesson.id"
                            class="border-b border-gray-200 px-4 py-3 last:border-b-0"
                        >
                            <div class="flex items-start justify-between gap-4">
                                <div class="flex-1">
                                    <div
                                        class="text-sm font-semibold text-gray-700"
                                    >
                                        {{ lesson.lessonName }}
                                    </div>
                                    <div class="text-xs text-gray-500 mt-1">
                                        {{ lesson.path }}
                                    </div>
                                </div>

                                <span
                                    v-if="lesson.status === 'draft'"
                                    class="inline-block rounded-full bg-yellow-200 text-yellow-600 px-3 py-1 text-xs font-medium text-gray-700"
                                >
                                    {{ lesson.status.toUpperCase() }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Delete Modal Popup -->
            <div
                v-if="isDeleteModalOpen"
                class="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50 p-4"
                role="dialog"
                aria-modal="true"
                aria-labelledby="delete-modal-title"
                @click.self="closeDeleteModal"
            >
                <div
                    class="relative z-50 w-full max-w-lg rounded-lg bg-white p-6 shadow-2xl"
                >
                    <button
                        type="button"
                        class="absolute right-3 top-3 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                        aria-label="Close delete confirmation modal"
                        @click.prevent.stop="closeDeleteModal"
                    >
                        <span
                            aria-hidden="true"
                            class="block text-2xl leading-none"
                            >&times;</span
                        >
                    </button>

                    <h3
                        id="delete-modal-title"
                        class="pr-10 text-lg font-bold text-gray-900"
                    >
                        Are you sure you want to delete this resource?
                    </h3>
                    <p class="mt-2 text-base text-gray-600">
                        The resource will be removed from this list. Uploaded
                        files are kept in storage.
                    </p>

                    <div class="mt-6 flex justify-end gap-3">
                        <button
                            type="button"
                            class="rounded bg-gray-100 px-6 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
                            :disabled="isDeletingResource"
                            @click="closeDeleteModal"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            class="rounded bg-red-600 px-6 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                            :disabled="isDeletingResource"
                            @click="confirmDeleteResource"
                        >
                            {{
                                isDeletingResource
                                    ? "Deleting..."
                                    : "Delete Resource"
                            }}
                        </button>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script lang="ts">
import axios from "axios";
import { defineComponent, type PropType } from "vue";
import AdminFiltersBar from "../shared/AdminFiltersBar.vue";
import AdminPaginationControls from "../shared/AdminPaginationControls.vue";
import { InformationCircleIcon } from "@heroicons/vue/20/solid";

type ResourceLesson = {
    id: string;
    lessonName: string;
    status: string;
    path: string;
};

type ResourceItem = {
    id: number;
    title: string;
    slug: string;
    type: string;
    status: string;
    lesson_count: number;
    module_count: number;
    course_count: number;
    lessons: ResourceLesson[];
};

type ResourceIcon = {
    icon: string;
    bgColor: string;
};

export default defineComponent({
    components: {
        AdminFiltersBar,
        AdminPaginationControls,
        InformationCircleIcon,
    },
    props: {
        getType: {
            type: String,
            default: "",
        },
        getPageSize: {
            type: String,
            default: "10",
        },
        getPage: {
            type: String,
            default: "1",
        },
        getKeyword: {
            type: String,
            default: "",
        },
        getResources: {
            type: Array as PropType<ResourceItem[]>,
            default: () => [],
        },
    },
    data() {
        return {
            filtersState: {
                type: "",
                keyword: "",
            },
            initialPage: 1,
            initialPageSize: 10,
            isLessonsModalOpen: false,
            isDeleteModalOpen: false,
            isDeletingResource: false,
            selectedResource: null as ResourceItem | null,
            selectedDeleteResource: null as ResourceItem | null,
            allResources: this.getResources || [],
            icons: {
                flashcards: {
                    icon: "square-3-stack-3d",
                    bgColor: "bg-yellow-100",
                },
                interactive_images: { icon: "photo", bgColor: "bg-red-100" },
                pdf: { icon: "document", bgColor: "bg-green-100" },
                slides: { icon: "led-tv", bgColor: "bg-blue-100" },
            },
        };
    },
    computed: {
        filtersConfig(): any[] {
            return [
                {
                    key: "type",
                    label: "Resource Type",
                    type: "select",
                    widthClass: "flex w-1/2 flex-col",
                    options: [
                        { label: "All", value: "" },
                        { label: "Flashcards", value: "flashcards" },
                        {
                            label: "Interactive Images",
                            value: "interactive_images",
                        },
                        { label: "PDF", value: "pdf" },
                        { label: "Reference", value: "reference" },
                        { label: "Slides", value: "slides" },
                    ],
                },
                {
                    key: "keyword",
                    label: "Search Resources",
                    type: "text",
                    placeholder: "Search by title or slug",
                    ariaLabel: "Search Resources by title or slug",
                    widthClass: "flex w-1/2 flex-col",
                },
            ];
        },
        selectedResourceLessons(): ResourceLesson[] {
            return this.selectedResource?.lessons || [];
        },
        filteredResources(): ResourceItem[] {
            return this.allResources.filter((r: ResourceItem) => {
                const matchType =
                    !this.filtersState.type ||
                    r.type === this.filtersState.type;
                const matchKeyword =
                    !this.filtersState.keyword ||
                    (r.title || "")
                        .toLowerCase()
                        .includes(this.filtersState.keyword.toLowerCase()) ||
                    (r.slug || "")
                        .toLowerCase()
                        .includes(this.filtersState.keyword.toLowerCase());
                return matchType && matchKeyword;
            });
        },
    },
    methods: {
        emitToast(action: string, heading: string, status: string): void {
            (this as any).$events.$emit("toastNotification", {
                action,
                heading,
                status,
            });
        },
        resourceIcon(type: string): ResourceIcon {
            return (
                (this.icons as Record<string, ResourceIcon>)[type] ?? {
                    icon: "document",
                    bgColor: "bg-gray-100",
                }
            );
        },
        statusBadge(status: string): string {
            switch (status) {
                case "processing":
                    return "bg-yellow-200 text-yellow-700";
                case "failed":
                    return "bg-red-200 text-red-700";
                default:
                    return "bg-green-200 text-green-700";
            }
        },
        attachmentCount(resource: ResourceItem): number {
            return (
                (resource.lesson_count || 0) +
                (resource.module_count || 0) +
                (resource.course_count || 0)
            );
        },
        deleteTooltip(resource: ResourceItem): string {
            if (this.attachmentCount(resource) === 0) {
                return "Delete Resource";
            }

            return `Cannot delete: attached to ${resource.lesson_count} lesson(s), ${resource.module_count} module(s), ${resource.course_count} course(s)`;
        },
        openLessonsModal(resource: ResourceItem) {
            this.selectedResource = resource;
            this.isLessonsModalOpen = true;
        },
        closeLessonsModal(): void {
            this.isLessonsModalOpen = false;
        },
        handleEscape(event: KeyboardEvent): void {
            if (event.key === "Escape" && this.isLessonsModalOpen) {
                this.closeLessonsModal();
            }

            if (event.key === "Escape" && this.isDeleteModalOpen) {
                this.closeDeleteModal();
            }
        },
        openDeleteModal(resource: ResourceItem): void {
            if (this.attachmentCount(resource) > 0) {
                return;
            }

            this.selectedDeleteResource = resource;
            this.isDeleteModalOpen = true;
        },
        closeDeleteModal(): void {
            if (this.isDeletingResource) {
                return;
            }

            this.isDeleteModalOpen = false;
            this.selectedDeleteResource = null;
        },
        async confirmDeleteResource(): Promise<void> {
            if (!this.selectedDeleteResource) {
                return;
            }

            this.isDeletingResource = true;

            try {
                const response = await axios.delete(
                    `/api/admin/content/learn/resources/${this.selectedDeleteResource.id}`
                );

                if (response.status === 204) {
                    this.allResources = this.allResources.filter(
                        (resource: ResourceItem) =>
                            resource.id !== this.selectedDeleteResource?.id
                    );

                    this.emitToast(
                        "saved",
                        "Resource deleted successfully",
                        "success"
                    );

                    this.isDeleteModalOpen = false;
                    this.selectedDeleteResource = null;
                }
            } catch (error) {
                this.emitToast("error", "Failed to delete resource", "error");
            } finally {
                this.isDeletingResource = false;
            }
        },
    },
    mounted() {
        this.filtersState.type = this.getType || "";
        this.filtersState.keyword = this.getKeyword || "";
        this.initialPageSize = Number(this.getPageSize || 10);
        this.initialPage = Number(this.getPage || 1);
        window.addEventListener("keydown", this.handleEscape);
    },
    beforeUnmount() {
        window.removeEventListener("keydown", this.handleEscape);
    },
});
</script>

<style>
.tippy-box[data-theme~="resource-lessons-tooltip"] .tippy-content {
    text-transform: none;
    font-weight: 400;
}
</style>
